import { getPorts, NODE_TYPE_LABELS, type NodeType, summarizeNode } from "@velizhanin-dev/bot-scenario"
import { ArrowUpRight } from "lucide-react"
import { toast } from "sonner"
import type { DraftNode, ScenarioDraft } from "@/entities/bot-builder"
import { CREATABLE_BLOCKS, placeNewNode } from "@/entities/bot-builder"
import { Button } from "@/shared/ui/button"
import { NativeSelect } from "@/shared/ui/native-select"
import { ToolButton } from "@/shared/ui/tool-button"
import type { DraftActions } from "../../model/use-scenario-draft"

const NEW_BLOCK_PREFIX = "new:"
const OPTION_LENGTH = 60

const shorten = (text: string) =>
	text.length > OPTION_LENGTH ? `${text.slice(0, OPTION_LENGTH - 1)}…` : text

interface NodeLinksProps {
	draft: ScenarioDraft
	node: DraftNode
	actions: Pick<DraftActions, "putEdge" | "deleteEdge" | "createNode">
	onSelectNode: (nodeId: string) => void
}

/**
 * Связи блока без проводов: куда ведёт каждый выход: существующий блок потока
 * или новый, и какие блоки ведут сюда. Главный способ связывать на телефоне.
 */
export function NodeLinks({ draft, node, actions, onSelectNode }: NodeLinksProps) {
	const ports = getPorts(node)
	const describe = (target: DraftNode) =>
		`${NODE_TYPE_LABELS[target.type as NodeType] ?? target.type}: ${summarizeNode(target, draft.flows, draft.tags)}`
	const candidates = draft.nodes.filter(
		(candidate) =>
			candidate.flowId === node.flowId && candidate.id !== node.id && candidate.type !== "start",
	)
	const incoming = [
		...new Map(
			draft.edges
				.filter((edge) => edge.targetNodeId === node.id)
				.flatMap((edge) => {
					const source = draft.nodes.find((item) => item.id === edge.sourceNodeId)
					return source ? [[source.id, source] as const] : []
				}),
		).values(),
	]
	if (ports.length === 0 && incoming.length === 0) return null

	const connect = async (portId: string, value: string) => {
		const current = draft.edges.find(
			(edge) => edge.sourceNodeId === node.id && edge.sourcePort === portId,
		)
		// С одного выхода: одна связь: api сам заменяет прежнюю, и при новом блоке тоже.
		if (value.startsWith(NEW_BLOCK_PREFIX)) {
			const type = value.slice(NEW_BLOCK_PREFIX.length) as Exclude<NodeType, "start">
			const result = await actions.createNode({
				flowId: node.flowId,
				type,
				position: placeNewNode(draft, node.flowId, node),
				connectFrom: { nodeId: node.id, port: portId },
			})
			if (result) {
				toast.success(`Добавлен блок «${NODE_TYPE_LABELS[type]}»`)
				onSelectNode(result.node.id)
			}
			return
		}
		if (current?.targetNodeId === value) return
		if (!value) {
			if (current) await actions.deleteEdge(current.id)
			return
		}
		await actions.putEdge({ sourceNodeId: node.id, sourcePort: portId, targetNodeId: value })
	}

	return (
		<section className="space-y-3 rounded-xl border border-border/60 p-3">
			<h3 className="text-[13px] font-medium">Связи</h3>
			{ports.map((port) => {
				const current = draft.edges.find(
					(edge) => edge.sourceNodeId === node.id && edge.sourcePort === port.id,
				)
				return (
					<div key={port.id} className="space-y-1">
						<p className="text-xs text-muted-foreground">{port.label} →</p>
						<div className="flex gap-1.5">
							<NativeSelect
								className="min-w-0 flex-1"
								aria-label={`Куда ведёт «${port.label}»`}
								value={current?.targetNodeId ?? ""}
								onChange={(event) => void connect(port.id, event.target.value)}
							>
								<option value="">Не подключено</option>
								{candidates.length > 0 ? (
									<optgroup label="Блоки воронки">
										{candidates.map((candidate) => (
											<option key={candidate.id} value={candidate.id}>
												{shorten(describe(candidate))}
											</option>
										))}
									</optgroup>
								) : null}
								<optgroup label="Новый блок">
									{CREATABLE_BLOCKS.map((block) => (
										<option key={block.type} value={`${NEW_BLOCK_PREFIX}${block.type}`}>
											+ {NODE_TYPE_LABELS[block.type]}
										</option>
									))}
								</optgroup>
							</NativeSelect>
							{current ? (
								<ToolButton
									label="Открыть следующий блок"
									onClick={() => onSelectNode(current.targetNodeId)}
								>
									<ArrowUpRight />
								</ToolButton>
							) : null}
						</div>
					</div>
				)
			})}
			{incoming.length > 0 ? (
				<div className="space-y-1.5 border-t border-border/60 pt-2.5">
					<p className="text-xs text-muted-foreground">Сюда ведут</p>
					<div className="flex flex-wrap gap-1">
						{incoming.map((source) => (
							<Button
								key={source.id}
								type="button"
								variant="ghost"
								onClick={() => onSelectNode(source.id)}
								className="h-auto max-w-full justify-start truncate rounded-md bg-muted/60 px-2 py-1 text-left text-xs hover:bg-muted"
							>
								{shorten(describe(source))}
							</Button>
						))}
					</div>
				</div>
			) : null}
		</section>
	)
}
